<template>
  <div data-component="app-launcher">
    <app-launcher-header
      v-if="showAppHeader"
      :avatar="avatar"
      :agent="agent"
      :actions-type="actionsType"
      :app-version="appVersion"
      :is-updating="isUpdating"
      @go-back="$emit('go-back')"
      @option-click="$emit('option-click', $event)"
      @save="$emit('save')"
      @update="$emit('update')"
      @cancel="$emit('cancel')"
      @sign-out="$emit('sign-out')"
    />

    <base-content-loader
      v-if="showAppContentLoader"
      :title="$t('HITL.common.app-title')"
      :description="description"
    />

    <div
      v-else
      class="content"
      :class="{ 'is-center-content': !isUsingBundle }"
    >
      <slot name="default" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';

import AppLauncherHeader from './AppLauncherHeader.vue';
import { BaseContentLoader } from '@/modules/common/components/common';
import {
  APP_STATES,
  AVATAR_PROP,
  AGENT_PROP,
  APP_VERSION_PROP,
  APP_STATE_PROP,
  ACTIONS_TYPE_PROP,
} from '../constants';

/** Main component is a content wrapper with header and loading screens */
export default defineComponent({
  name: 'AppLauncher',
  components: {
    BaseContentLoader,
    AppLauncherHeader,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    actionsType: ACTIONS_TYPE_PROP,
    appVersion: APP_VERSION_PROP,
    appState: APP_STATE_PROP,
    isInitializing: Boolean,
    isLoadingBundle: Boolean,
    isLoadingSettings: Boolean,
    isUpdating: Boolean,
  },
  emits: ['go-back', 'option-click', 'save', 'update', 'cancel', 'sign-out'],
  computed: {
    description() {
      switch (true) {
        case this.isInitializing:
          return this.$t('HITL.app-launcher.initialize-description');
        case this.isLoadingBundle:
          return this.$t('HITL.app-launcher.initialize-bundle-description');
        case this.isLoadingSettings:
          return this.$t('HITL.app-launcher.initialize-settings-description');
        default:
          return '';
      }
    },
    isUsingBundle() {
      return this.appState === APP_STATES.APP_BUNDLE;
    },
    showAppHeader() {
      return !this.isUsingBundle && !this.isInitializing && !this.isLoadingBundle;
    },
    showAppContentLoader() {
      return this.isInitializing || this.isLoadingBundle || this.isLoadingSettings;
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='app-launcher'] {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: var(--or-c-surface-1);
  overflow: hidden;

  [data-component='app-launcher-header'] {
    flex: 0 0 auto;
  }

  .content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    max-height: 100%;
    overflow: hidden;

    &.is-center-content {
      align-items: center;
      justify-content: center;
      padding: var(--s-5);
    }
  }
}
</style>
